/* The top bar's rotation: an oscilloscope redraw, not a fade.
 *
 * Two message elements share the lane. On each cycle the incoming one is
 * revealed by a clip-path wipe while the beam element rides the reveal edge,
 * then roles swap. The cycle track refills over the interval so the rotation
 * has a visible clock. Hover holds everything.
 *
 * Fails quiet: without JS the first message renders statically.
 */
(function () {
  'use strict';

  if (customElements.get('ak-topbar')) return;

  var SWEEP_MS = 750;

  customElements.define('ak-topbar', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = '1';

      var raw = this.querySelector('[data-ak-topbar-messages]');
      try {
        this.messages = JSON.parse(raw ? raw.textContent : '[]');
      } catch (e) {
        this.messages = [];
      }

      this.a = this.querySelector('[data-ak-top-a]');
      this.b = this.querySelector('[data-ak-top-b]');
      this.beam = this.querySelector('[data-ak-top-beam]');
      this.cycleEl = this.querySelector('[data-ak-top-cycle]');
      this.lane = this.querySelector('.ak-topbar__lane');
      this.current = 0;
      this.intervalMs = (Number(this.dataset.interval) || 6) * 1000;
      this.paused = false;
      this.sweeping = false;

      var self = this;

      // Overflow watch applies even to a single static message.
      this.fitMessage(this.a);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { self.fitActive(); });
      }
      var resizeWait = null;
      window.addEventListener('resize', function () {
        window.clearTimeout(resizeWait);
        resizeWait = window.setTimeout(function () { self.fitActive(); }, 150);
      }, { passive: true });

      if (this.messages.length < 2) return;
      /* Hover-pause is for pointers that can actually hover. On a touch
         screen mouseenter fires on tap and mouseleave often never arrives,
         which left the fill frozen wherever the finger landed. */
      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (canHover) {
        this.addEventListener('mouseenter', function () { self.setPaused(true); });
        this.addEventListener('mouseleave', function () { self.setPaused(false); });
      }

      /* Keyboard focus is safe either way: focusin and focusout always pair. */
      this.addEventListener('focusin', function () { self.setPaused(true); });
      this.addEventListener('focusout', function () { self.setPaused(false); });

      /* Belt and braces for anything else that manages to leave it paused on
         a touch device: a finished touch always releases it. */
      this.addEventListener('touchend', function () { self.setPaused(false); }, { passive: true });
      this.addEventListener('touchcancel', function () { self.setPaused(false); }, { passive: true });

      /* The bar IS the clock. Driving the rotation from its own animationend
         means the fill and the message can never fall out of step, so the bar
         always runs a complete sweep and then hands over. */
      if (this.cycleEl) {
        this.cycleEl.addEventListener('animationend', function () {
          if (self.paused || self.sweeping) return;
          self.advance();
        });
      }

      /* A backgrounded tab has its animations throttled or dropped, so the
         fill comes back part-drawn and out of step. Start it again rather
         than letting it resume from wherever it stopped. */
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible' || self.paused) return;
        self.startCycle();
      });

      this.startCycle();
    }

    setPaused(paused) {
      this.paused = paused;
      if (this.cycleEl) {
        this.cycleEl.style.animationPlayState = paused ? 'paused' : 'running';
      }
    }

    /* Each message earns its own time on stage: at least the configured
       interval, and never less than its full slide when it overflows, so a
       long sentence always finishes before the bar moves on. */
    dwellFor(el) {
      if (el && el.classList.contains('is-overflowing')) {
        var slideS = parseFloat(el.style.getPropertyValue('--ak-slide-dur')) || 0;
        return Math.max(this.intervalMs, slideS * 1000 + 500);
      }
      return this.intervalMs;
    }

    startCycle() {
      var dwell = this.dwellFor(this.a);
      this.runCycleBar(dwell);
      this.schedule(dwell);
    }

    /* Backstop only. animationend normally advances the rotation; this covers
       the cases where it never fires: reduced motion turning the animation
       off, or a tab backgrounded long enough for the browser to drop it. The
       margin keeps it out of the way of the real trigger, and the retry is
       fine enough that a genuine wait is never visible as a stall. */
    schedule(ms) {
      var self = this;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(function tick() {
        if (self.paused || self.sweeping) {
          self.timer = window.setTimeout(tick, 60);
          return;
        }
        self.advance();
      }, ms + 600);
    }

    runCycleBar(ms) {
      if (!this.cycleEl) return;
      this.cycleEl.style.animationDuration = ms + 'ms';
      this.cycleEl.classList.remove('is-running');
      void this.cycleEl.offsetWidth;
      this.cycleEl.classList.add('is-running');
    }

    fill(el, message) {
      var inner = document.createElement('span');
      inner.className = 'ak-topbar__msg-inner';
      if (message.url) {
        var link = document.createElement('a');
        link.href = message.url;
        link.textContent = message.text;
        inner.appendChild(link);
      } else {
        inner.textContent = message.text;
      }
      el.replaceChildren(inner);
    }

    /* Slide only when the text genuinely does not fit. Distance is the
       exact overflow; pace scales with it so long messages read calmly. */
    fitMessage(el) {
      if (!el || !this.lane) return;
      var inner = el.querySelector('.ak-topbar__msg-inner');
      el.classList.remove('is-overflowing');
      el.style.removeProperty('--ak-slide-x');
      el.style.removeProperty('--ak-slide-dur');
      if (!inner) return;
      var over = inner.scrollWidth - this.lane.clientWidth;
      if (over > 2) {
        el.style.setProperty('--ak-slide-x', -over + 'px');
        /* The keyframes hold the opening for the first 24% of the cycle,
           so the cycle has to be long enough that 24% is actually readable:
           at the 5s floor that is 1.2s, and at the 11s cap it is 2.6s. The
           dwell below scales off this, so a longer slide also stays on
           stage longer. */
        var dur = Math.min(11, Math.max(5, 4.5 + over * 0.025));
        el.style.setProperty('--ak-slide-dur', dur + 's');
        el.classList.add('is-overflowing');
      }
    }

    fitActive() {
      this.fitMessage(this.querySelector('.ak-topbar__msg.is-active'));
    }

    advance() {
      var self = this;
      this.sweeping = true;
      this.current = (this.current + 1) % this.messages.length;

      var outgoing = this.a.classList.contains('is-active') ? this.a : this.b;
      var incoming = outgoing === this.a ? this.b : this.a;

      this.fill(incoming, this.messages[this.current]);
      this.fitMessage(incoming);
      incoming.removeAttribute('aria-hidden');

      // The wipe: incoming opens left to right while outgoing clears behind
      // the same edge, so both stay transparent and the aurora shows through.
      incoming.classList.add('is-active', 'is-sweeping');
      outgoing.classList.add('is-clearing');
      this.beam.classList.add('is-sweeping');

      window.setTimeout(function () {
        incoming.classList.remove('is-sweeping');
        self.beam.classList.remove('is-sweeping');
        outgoing.classList.remove('is-active', 'is-clearing');
        outgoing.setAttribute('aria-hidden', 'true');
        self.sweeping = false;
      }, SWEEP_MS + 40);

      var dwell = this.dwellFor(incoming);
      this.runCycleBar(dwell);
      this.schedule(dwell);
    }
  });
})();
